import { useEffect, useState } from 'react';
import { apiBase, authHeaders } from '../utils/authHeaders';
import { apiErrorMessage } from '../utils/apiError';

export function currentSiteId() {
  try { return localStorage.getItem('current_location_id') || ''; } catch { return ''; }
}

/** One call to /api/vision with the selected site; errors carry the HTTP status. */
export async function visionCall(path, { method = 'GET', body, signal } = {}) {
  const response = await fetch(`${apiBase()}/api/vision${path}`, {
    method,
    signal,
    headers: authHeaders({ locationId: currentSiteId(), json: body !== undefined }),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(apiErrorMessage(data.detail, `Request failed (${response.status})`));
    error.status = response.status;
    throw error;
  }
  return data;
}

/** Nav visibility: true only when the module is switched on for this organisation and a site is selected. */
export function useVisionNavEnabled(token, probe = true) {
  const [site, setSite] = useState(currentSiteId);
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const update = () => setSite(currentSiteId());
    window.addEventListener('locationChanged', update);
    window.addEventListener('organizationChanged', update);
    return () => {
      window.removeEventListener('locationChanged', update);
      window.removeEventListener('organizationChanged', update);
    };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    setEnabled(false);
    if (probe && token && site) {
      visionCall('/capabilities', { signal: controller.signal })
        .then(() => { if (!controller.signal.aborted) setEnabled(true); })
        .catch(() => {});
    }
    return () => controller.abort();
  }, [token, site, probe]);
  return enabled;
}

export const STATUS_LABELS = {
  candidate: 'Candidate',
  review_required: 'Needs review',
  inconclusive: 'Inconclusive',
  confirmed: 'Confirmed',
  dismissed: 'Dismissed',
  action_assigned: 'Check assigned',
  resolution_submitted: 'Awaiting sign-off',
  closed: 'Closed',
};

export const TABS = [
  { id: 'review', label: 'Review', statuses: ['review_required', 'inconclusive'] },
  { id: 'actions', label: 'Open checks', statuses: ['confirmed', 'action_assigned', 'resolution_submitted'] },
  { id: 'closed', label: 'Closed', statuses: ['closed', 'dismissed'] },
];

export function tabForStatus(status) {
  return (TABS.find((tab) => tab.statuses.includes(status)) || TABS[0]).id;
}

/** What the camera did and did not establish, in plain words. */
export const REASON_TEXT = {
  observed: 'Seen for long enough, at the configured confidence.',
  partial_view: 'Seen, but only part of the view was clear. A person needs to look.',
  obscured_view: 'The view was obscured, so nothing can be concluded. This is not a breach and not a pass.',
};

/** Mirrors backend workflow.TRANSITIONS. `field` is the one input the action needs. */
export const ACTIONS_BY_STATUS = {
  review_required: [
    { action: 'confirm', label: 'Confirm concern' },
    { action: 'dismiss', label: 'Dismiss', field: 'reason', prompt: 'Reason (needed to dismiss or mark inconclusive)' },
    { action: 'mark_inconclusive', label: 'Mark inconclusive', field: 'reason', prompt: 'Reason (needed to dismiss or mark inconclusive)' },
  ],
  inconclusive: [{ action: 'queue_for_review', label: 'Send back for review' }],
  confirmed: [{ action: 'assign_action', label: 'Assign a check', field: 'assigned_to_user_id', prompt: 'User id to assign to' }],
  action_assigned: [{ action: 'submit_resolution', label: 'Submit what was done', field: 'note', prompt: 'What was done?' }],
  resolution_submitted: [
    { action: 'close', label: 'Close (second person)' },
    { action: 'reopen', label: 'Reopen', field: 'reason', prompt: 'What is still outstanding?' },
  ],
};

export const SEVERITY_CLASS = {
  high: 'bg-red-100 text-red-900',
  medium: 'bg-amber-100 text-amber-900',
  low: 'bg-gray-100 text-gray-800',
};
